import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'
import { Plus, Edit, Trash2, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table'
import { Badge } from '../components/ui/Badge'
import { brandsApi } from '../lib/api'
import { formatNumber } from '../lib/utils'
import { useLanguage } from '../contexts/LanguageContext'

export default function Brands() {
  const [filters, setFilters] = useState({
    search: '',
    page: 1,
    limit: 10
  })
  const [deleteId, setDeleteId] = useState(null)
  const { currentLanguage } = useLanguage()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery(
    ['brands', filters],
    () => brandsApi.getAll(filters),
    {
      select: (data) => data.data,
      keepPreviousData: true,
      retry: 1,
      refetchOnWindowFocus: false
    }
  )

  const deleteMutation = useMutation(
    (id) => brandsApi.delete(id),
    {
      onSuccess: () => {
        toast.success('Brand deleted successfully')
        queryClient.invalidateQueries(['brands'])
        setDeleteId(null)
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete brand')
      }
    }
  )

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }))
  }

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this brand?')) {
      deleteMutation.mutate(id)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Brands</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage car manufacturers and brands
          </p>
        </div>
        <Link to="/brands/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Brand
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Search className="h-5 w-5 mr-2" />
            Search Brands
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-w-md">
            <Input
              placeholder="Search brands..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Brands ({data?.count || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-center py-12">
              <p className="text-red-600">Error loading brands: {error.message}</p>
            </div>
          ) : isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : data?.results?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Vehicles</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.results.map((brand) => (
                  <TableRow key={brand.id}>
                    <TableCell className="font-medium">{brand.name}</TableCell>
                    <TableCell>{brand.code}</TableCell>
                    <TableCell>{brand.slug}</TableCell>
                    <TableCell>{formatNumber(brand.vehicle_count)}</TableCell>
                    <TableCell>
                      <Badge variant={brand.is_active ? 'success' : 'gray'}>
                        {brand.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Link to={`/brands/${brand.id}/edit`}>
                          <Button variant="ghost" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(brand.id)}
                          disabled={deleteMutation.isLoading && deleteId === brand.id}
                        >
                          {deleteMutation.isLoading && deleteId === brand.id ? (
                            <div className="loading-spinner" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <h3 className="mt-2 text-sm font-medium text-gray-900">No brands</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by adding your first brand.
              </p>
              <div className="mt-6">
                <Link to="/brands/new">
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Brand
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
